import React, { useState, useMemo, useRef, useEffect } from "react";
import { toast } from "sonner";
import { useBucketCollections } from "../hooks/useBucketCollections";
import { formatCurrency, formatPercentage } from "../sdk/utils/display";
import {
  LoadingMessage,
  ErrorMessage,
  EmptyMessage,
  WarningMessage,
  LayoutHeader,
  TableHeaderCell,
} from "./smartlayout/fragments";
import { TransferModal } from "./smartlayout/modals";
import {
  DEFAULT_BASE_URL,
  DEFAULT_HEIGHT_BUCKET_COLLECTIONS,
  DEFAULT_REFRESH_INTERVAL,
} from "./smartlayout/constants";
import { ExchangeClient } from "../sdk/api/exchange/ExchangeClient";
import "./smartlayout/layout.css";
import { toSatoshi } from "../sdk";

interface BucketCollectionsProps {
  baseURL?: string;
  authToken?: string;
  walletAddress?: string;
  mnemonic?: string; // Required for transfer operations
  height?: string;
  refreshInterval?: number;
  onBucketSelect?: (bucketId: string, nominalValue: number) => void;
}

type SortField =
  | "marginType"
  | "maintenanceMargin"
  | "nominalValue"
  | "riskRatio"
  | "bucketId";
type SortDirection = "asc" | "desc";

function BucketCollections({
  baseURL = DEFAULT_BASE_URL,
  authToken,
  walletAddress,
  mnemonic,
  height = DEFAULT_HEIGHT_BUCKET_COLLECTIONS,
  refreshInterval = DEFAULT_REFRESH_INTERVAL,
  onBucketSelect,
}: BucketCollectionsProps): JSX.Element {
  const { collections, loading, error, spotWalletBalance, refresh } =
    useBucketCollections({
      baseURL,
      authToken,
      walletAddress,
      refreshInterval,
    });

  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  const [transferFrom, setTransferFrom] = useState<string>("");
  const [transferTo, setTransferTo] = useState<string>("");
  const [transferAmount, setTransferAmount] = useState<number>(10);
  const [createBucketAmount, setCreateBucketAmount] = useState<number>(10);
  const [activeTab, setActiveTab] = useState<"transfer" | "create">("transfer");
  const [isTransferring, setIsTransferring] = useState<boolean>(false);
  const exchangeClientRef = useRef<ExchangeClient | null>(null);

  // Sort collections
  const sortedCollections = useMemo(() => {
    if (!sortField) return collections;

    return [...collections].sort((a, b) => {
      let aValue: number | string = 0;
      let bValue: number | string = 0;

      switch (sortField) {
        case "marginType":
          aValue = a.marginType || "";
          bValue = b.marginType || "";
          break;
        case "maintenanceMargin":
          aValue = a.maintenanceMargin || 0;
          bValue = b.maintenanceMargin || 0;
          break;
        case "nominalValue":
          aValue = a.nominalValue || 0;
          bValue = b.nominalValue || 0;
          break;
        case "riskRatio":
          aValue = a.riskRatio || 0;
          bValue = b.riskRatio || 0;
          break;
        case "bucketId":
          aValue = a.bucketId || "";
          bValue = b.bucketId || "";
          break;
      }

      if (typeof aValue === "string" && typeof bValue === "string") {
        return sortDirection === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      const numA = Number(aValue);
      const numB = Number(bValue);

      return sortDirection === "asc" ? numA - numB : numB - numA;
    });
  }, [collections, sortField, sortDirection]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
  };

  const handleRowClick = (bucketId: string, nominalValue: number) => {
    if (onBucketSelect) {
      onBucketSelect(bucketId, nominalValue);
    }
  };

  const handleTransferClick = () => {
    setIsTransferModalOpen(true);
    setTransferFrom("");
    setTransferTo("");
    setTransferAmount(10);
    setCreateBucketAmount(10);
    setActiveTab("transfer");
  };

  const handleTransferCancel = () => {
    setIsTransferModalOpen(false);
    setTransferFrom("");
    setTransferTo("");
    setTransferAmount(10);
    setCreateBucketAmount(10);
    setActiveTab("transfer");
  };

  // Initialize ExchangeClient when mnemonic is available
  useEffect(() => {
    if (!mnemonic || !baseURL) {
      exchangeClientRef.current = null;
      return;
    }

    const exchangeClient = new ExchangeClient({
      baseURL,
      mnemonic,
    });

    exchangeClient
      .initialize()
      .then(() => {
        exchangeClientRef.current = exchangeClient;
      })
      .catch((err) => {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "Failed to initialize exchange client";
        console.error("Failed to initialize ExchangeClient:", err);
        toast.error("Exchange client initialization failed", {
          description: errorMessage,
        });
        exchangeClientRef.current = null;
      });
  }, [mnemonic, baseURL]);

  const handleTransferConfirm = async () => {
    // Check if mnemonic is provided first
    if (!mnemonic) {
      toast.error("Mnemonic required", {
        description:
          "Mnemonic is required to perform transfers. Please provide a mnemonic to enable transfer functionality.",
      });
      return;
    }

    // Check if ExchangeClient is initialized
    const exchangeClient = exchangeClientRef.current;
    if (!exchangeClient) {
      toast.error("Exchange client not ready", {
        description:
          "Exchange client is still initializing. Please wait a moment and try again.",
      });
      return;
    }

    setIsTransferring(true);

    try {
      const assetId = "USDC"; // Using USDC as specified
      let result;
      let successMessage = "";

      // Convert transferAmount to satoshi
      const transferAmountSatoshi = toSatoshi(transferAmount);

      if (activeTab === "transfer") {
        // Transfer tab: Handle transfers between buckets or spot wallet
        if (!transferFrom || !transferTo) {
          throw new Error("Please select both source and destination");
        }

        if (transferFrom === "spot-wallet" && transferTo === "spot-wallet") {
          throw new Error("Cannot transfer from spot wallet to spot wallet");
        }

        if (transferFrom === "spot-wallet") {
          // Spot wallet → Bucket: extractToBucket
          result = await exchangeClient.extractToBucket({
            bucketId: transferTo,
            assetId,
            amount: transferAmountSatoshi,
          });
          successMessage = `Successfully transferred ${formatCurrency(transferAmount)} from Spot Wallet to ${transferTo}`;
        } else if (transferTo === "spot-wallet") {
          // Bucket → Spot wallet: withdrawFromBucket
          result = await exchangeClient.withdrawFromBucket({
            bucketId: transferFrom,
            assetId,
            amount: transferAmountSatoshi,
          });
          successMessage = `Successfully transferred ${formatCurrency(transferAmount)} from ${transferFrom} to Spot Wallet`;
        } else {
          // Bucket → Bucket: bucketTransfer
          result = await exchangeClient.bucketTransfer({
            fromBucketId: transferFrom,
            toBucketId: transferTo,
            assetId,
            amount: transferAmountSatoshi,
          });
          successMessage = `Successfully transferred ${formatCurrency(transferAmount)} from ${transferFrom} to ${transferTo}`;
        }
      } else {
        // Create Bucket tab: Extract funds to create/initialize a new bucket
        if (!transferFrom) {
          throw new Error("Please select a source");
        }

        if (transferFrom === "spot-wallet") {
          // For create bucket from spot wallet, we need a bucket ID
          // Since the UI doesn't provide a target bucket, this might need special handling
          // For now, we'll show an error that create bucket requires a target bucket ID
          throw new Error(
            "Create Bucket from spot wallet requires a target bucket ID. Please use the Transfer tab to extract funds to an existing bucket, or create the bucket first.",
          );
        } else {
          // Create bucket from existing bucket - this would be a transfer to a new bucket
          // But we don't have a target bucket ID in the create bucket tab
          throw new Error(
            "Create Bucket requires a target bucket ID. Please use the Transfer tab to transfer between buckets.",
          );
        }
      }
      // Success - refresh data and close modal
      console.log("Transfer successful:", result);
      // Show success toast
      toast.success("Transfer completed", {
        description: successMessage,
      });
      // Refresh bucket collections to show updated balances
      refresh();
      // Close modal and reset state
      setIsTransferModalOpen(false);
      setTransferFrom("");
      setTransferTo("");
      setTransferAmount(10);
      setCreateBucketAmount(10);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Transfer failed";
      toast.error("Transfer failed", {
        description: errorMessage,
      });
      console.error("Transfer error:", err);
    } finally {
      setIsTransferring(false);
    }
  };

  // Prepare options for transfer modal - include spot wallet
  const transferOptions = useMemo(() => {
    const bucketOptions = sortedCollections.map((collection) => ({
      value: collection.bucketId,
      label: `${collection.bucketId} (${formatCurrency(collection.nominalValue)})`,
    }));
    // Add spot wallet option at the beginning
    return [
      {
        value: "spot-wallet",
        label: `Spot Wallet (${formatCurrency(spotWalletBalance)})`,
      },
      ...bucketOptions,
    ];
  }, [sortedCollections, spotWalletBalance]);

  // Prepare buckets data for transfer modal
  const bucketsData = useMemo(() => {
    return sortedCollections.map((collection) => ({
      bucketId: collection.bucketId,
      nominalValue: collection.nominalValue,
    }));
  }, [sortedCollections]);

  // Show appropriate message when wallet address is not available
  if (!walletAddress || walletAddress.trim() === "") {
    return (
      <WarningMessage message="Wallet address required to view bucket collections" />
    );
  }

  if (loading) {
    return <LoadingMessage message="Loading bucket collections..." />;
  }

  if (error) {
    return <ErrorMessage error={error} />;
  }

  return (
    <div className="layout-container" style={{ height }}>
      {/* Header */}
      <LayoutHeader title="BUCKET COLLECTIONS">
        <div onClick={handleTransferClick} className="btn-tab">
          <img src="/transfer-repu.svg" />
          <span>Transfer</span>
        </div>
      </LayoutHeader>

      {/* Table */}
      <div className="table-container shrinkable">
        {/* Table Header */}
        <div
          className="table-header"
          style={{ gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr" }}
        >
          <TableHeaderCell
            field="bucketId"
            currentSortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
          >
            Bucket ID
          </TableHeaderCell>
          <TableHeaderCell
            field="marginType"
            currentSortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
          >
            Margin Type
          </TableHeaderCell>
          <TableHeaderCell
            field="maintenanceMargin"
            currentSortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
          >
            Maintenance Margin
          </TableHeaderCell>
          <TableHeaderCell
            field="nominalValue"
            currentSortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
          >
            Balance
          </TableHeaderCell>
          <TableHeaderCell
            field="riskRatio"
            currentSortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
          >
            Risk Ratio
          </TableHeaderCell>
        </div>
        {/* Table Body */}
        {sortedCollections.length === 0 ? (
          <EmptyMessage message="No bucket collections" />
        ) : (
          sortedCollections.map((collection, index) => (
            <div
              key={`collection-${collection.bucketId}-${index}`}
              onClick={() =>
                handleRowClick(collection.bucketId, collection.nominalValue)
              }
              className={`table-row clickable ${index % 2 === 0 ? "even" : ""}`}
              style={{ gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr" }}
            >
              <div className="table-cell primary">{collection.bucketId}</div>
              <div className="table-cell">
                {collection.marginType.toUpperCase()}
              </div>
              <div className="table-cell right">
                {formatCurrency(collection.maintenanceMargin)}
              </div>
              <div className="table-cell right">
                {formatCurrency(collection.nominalValue)}
              </div>
              <div
                className="table-cell right"
                style={{
                  color:
                    (collection.riskRatio || 0) > 0.5 ? "#ff6b6b" : "#51cf66",
                  fontWeight: 500,
                }}
              >
                {formatPercentage(collection.riskRatio)}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Transfer Modal */}
      <TransferModal
        isOpen={isTransferModalOpen}
        fromValue={transferFrom}
        toValue={transferTo}
        options={transferOptions}
        bucketsData={bucketsData}
        transferAmount={transferAmount}
        createBucketAmount={createBucketAmount}
        onFromChange={setTransferFrom}
        onToChange={setTransferTo}
        onTransferAmountChange={setTransferAmount}
        onCreateBucketAmountChange={setCreateBucketAmount}
        onConfirm={handleTransferConfirm}
        onCancel={handleTransferCancel}
        spotWalletBalance={spotWalletBalance}
        activeTab={activeTab}
        onActiveTabChange={setActiveTab}
        isTransferring={isTransferring}
        onOpen={refresh}
      />
    </div>
  );
}

export default React.memo(BucketCollections);
